import { useState } from 'react';
import {
    mainFillType, gradientLinePoints, radialExtent,
} from '../utils/gradientHandles';
import { GRADIENT_ANGLE_DEFAULT } from '../panels/propertyControls';

const FIGMA_BLUE = '#0d99ff';

/**
 * Figma's gradient gizmo: the line that aims a linear fill, with a colour chip
 * at each end showing the stop it moves — the one being aimed wears the blue
 * ring — or the centre chip and extent ring of a radial fill. Drawn in the box's
 * own coordinates, so an element's gizmo inherits that element's rotation.
 *
 * `paint` is anything carrying the gradient keys: an element's `styles`, or a
 * frame, which keeps them at its top level.
 */
export default function GradientGizmo({ paint = {}, width, height, px, onHandleDown }) {
    // Which stop the admin is working on. Figma rings the selected one; the first
    // stop wears it until the other end is touched.
    const [active, setActive] = useState('start');

    const angle = Number(paint.gradientAngle) || GRADIENT_ANGLE_DEFAULT;
    const c1 = paint.gradientColor1 || '#ffffff';
    const c2 = paint.gradientColor2 || '#e4e4e7';

    /** The dot sitting exactly on the line's end — the point the maths uses. */
    const pin = (key, pt) => {
        const d = px(6);
        return (
            <div
                key={key}
                data-export-ignore="1"
                aria-hidden
                className="absolute bg-white pointer-events-none"
                style={{
                    width: d,
                    height: d,
                    left: pt.x - d / 2,
                    top: pt.y - d / 2,
                    borderRadius: '50%',
                    border: `${px(1)}px solid rgba(0,0,0,0.35)`,
                    zIndex: 9999,
                }}
            />
        );
    };

    /**
     * The colour chip: a square of the stop's own colour, turned to sit square
     * to the gradient line and pushed just past the end of it, the way Figma
     * hangs its stop swatches off the axis.
     */
    const chip = (key, pt, color, which, title) => {
        const s = px(20);
        // Push the chip outward along the line so it never covers the pin.
        const rad = (angle * Math.PI) / 180;
        const out = px(15) * (which === 'start' ? -1 : 1);
        const cx = pt.x + Math.sin(rad) * out;
        const cy = pt.y - Math.cos(rad) * out;
        const on = active === which;
        return (
            <div
                key={key}
                data-export-ignore="1"
                title={title}
                aria-label={title}
                className="absolute pointer-events-auto cursor-move"
                style={{
                    width: s,
                    height: s,
                    left: cx - s / 2,
                    top: cy - s / 2,
                    backgroundColor: color,
                    borderRadius: px(3),
                    border: `${px(on ? 2.5 : 2)}px solid ${on ? FIGMA_BLUE : '#ffffff'}`,
                    boxShadow: `0 0 0 ${px(0.5)}px rgba(0,0,0,0.25), 0 ${px(1)}px ${px(3)}px rgba(0,0,0,0.3)`,
                    transform: `rotate(${angle}deg)`,
                    zIndex: 10000,
                }}
                onPointerDown={(ev) => {
                    setActive(which);
                    onHandleDown(ev, which);
                }}
            />
        );
    };

    if (mainFillType(paint) === 'radial') {
        const cxPct = paint.gradientCenterX ?? 50;
        const cyPct = paint.gradientCenterY ?? 50;
        const cx = (cxPct / 100) * width;
        const cy = (cyPct / 100) * height;
        return (
            <>
                <svg
                    data-export-ignore="1"
                    className="absolute pointer-events-none overflow-visible"
                    style={{ left: 0, top: 0, width, height, zIndex: 9999 }}
                >
                    <circle
                        cx={cx}
                        cy={cy}
                        r={radialExtent(width, height, cxPct, cyPct)}
                        fill="none"
                        stroke="rgba(255,255,255,0.9)"
                        strokeWidth={px(1)}
                        strokeDasharray={`${px(4)} ${px(3)}`}
                    />
                </svg>
                {pin('grad-cp', { x: cx, y: cy })}
                {chip('grad-c', { x: cx, y: cy }, c1, 'center', 'Gradient centre — drag to move')}
            </>
        );
    }

    const line = gradientLinePoints(width, height, angle);
    return (
        <>
            <svg
                data-export-ignore="1"
                className="absolute pointer-events-none overflow-visible"
                style={{ left: 0, top: 0, width, height, zIndex: 9999 }}
            >
                {/* Dark under-stroke first: the white line has to read on top of
                    whatever colours the gradient itself is painting. */}
                <line
                    x1={line.start.x} y1={line.start.y}
                    x2={line.end.x} y2={line.end.y}
                    stroke="rgba(0,0,0,0.35)" strokeWidth={px(3)}
                />
                <line
                    x1={line.start.x} y1={line.start.y}
                    x2={line.end.x} y2={line.end.y}
                    stroke="#ffffff" strokeWidth={px(1.5)}
                />
            </svg>
            {pin('grad-sp', line.start)}
            {pin('grad-ep', line.end)}
            {chip('grad-s', line.start, c1, 'start', 'Gradient start colour — drag to aim')}
            {chip('grad-e', line.end, c2, 'end', 'Gradient end colour — drag to aim')}
        </>
    );
}
